type WithClientId<T> = T & { clientId?: string };

export type QuestionnaireChoiceForm = WithClientId<SpApi.Studio.IQuestionnaireChoicePost>;

export type QuestionnaireQuestionForm = WithClientId<
    Omit<SpApi.Studio.IQuestionnaireQuestionPost, 'choices' | 'responsePlaceholder'>
> & {
    choices?: QuestionnaireChoiceForm[];
    responsePlaceholder?: string | null;
};

export type QuestionnairePageForm = WithClientId<
    Omit<SpApi.Studio.IQuestionnairePagePost, 'questions'>
> & { questions: QuestionnaireQuestionForm[] };

/**
 * Minimal structural types that both Questionnaire and QuestionnaireTemplate
 * model variants satisfy. Use these in shared UI components so they accept
 * either flavour without casts.
 */
export type BaseChoiceForm = {
    id?: number;
    clientId?: string;
    choiceText: string;
    displayOrder: number;
};

export type BaseQuestionForm = {
    id?: number;
    clientId?: string;
    questionText: string;
    required: boolean;
    displayOrder: number;
    responseType: SpApi.Studio.QuestionnaireResponseType;
    responsePlaceholder?: string | null;
    choices?: BaseChoiceForm[];
};

export type BasePageForm = {
    id?: number;
    clientId?: string;
    name: string;
    displayOrder: number;
    questions: BaseQuestionForm[];
};

// Helper function to get a unique identifier for an entity (id or clientId)
export const getEntityId = (entity: { id?: number; clientId?: string }): string => {
    return entity.id?.toString() ?? entity.clientId ?? crypto.randomUUID();
};

export type QuestionnaireCreateFormValues = SpApi.Studio.IQuestionnaireCreatePayload & {
    contact: Pick<SpApi.Studio.IContact, 'id' | 'name'>;
};

export type QuestionnaireUpdateFormValues = Omit<
    SpApi.Studio.IQuestionnaireUpdatePayload,
    'dueDate' | 'pages'
> & {
    dueDate: Date | null;
    contact: Pick<SpApi.Studio.IContact, 'id' | 'name'>;
    pages: QuestionnairePageForm[];
};

export const responseTypeTranslationKeys: Record<SpApi.Studio.QuestionnaireResponseType, string> = {
    text_short: 'questionType.textShort',
    text_long: 'questionType.textLong',
    choices_dropdown: 'questionType.choicesDropdown',
    choices_radio: 'questionType.choicesRadio',
    choices_checkboxes: 'questionType.choicesCheckboxes'
};

export const hasChoices = (responseType: SpApi.Studio.QuestionnaireResponseType) => {
    return (
        responseType === 'choices_dropdown' ||
        responseType === 'choices_radio' ||
        responseType === 'choices_checkboxes'
    );
};

export const choiceSeparator = '|';

export const formatDateForApi = (date: Date | null | undefined) => {
    if (!date || Number.isNaN(date.getTime())) {
        return undefined;
    }

    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
};
